import { useEffect, useState } from "react";
import { getSummary, ApiError } from "../lib/api";
import ErrorBox from "./ErrorBox";
import MarkdownViewer from "../viewers/MarkdownViewer";

/**
 * Summary pane: pulls the existing audit summary.md for this preprint if one
 * exists. 404 is a normal case (most preprints don't have an audit yet) —
 * we render an explanatory message rather than an error.
 */
export default function SummaryPane(props: { egusphereId: string }) {
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setMarkdown(null);
    setNotFound(false);
    setError(null);
    (async () => {
      try {
        const s = await getSummary(props.egusphereId);
        if (!cancelled) setMarkdown(s.markdown);
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.egusphereId]);

  if (error)
    return (
      <div className="p-6">
        <ErrorBox title="Couldn't load the summary" detail={error} />
      </div>
    );
  if (notFound) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-sm text-ink-500">
        No reproducibility audit is available for this preprint yet.
      </div>
    );
  }
  if (markdown === null) return <div className="p-6 text-sm text-ink-500">Loading…</div>;
  return <MarkdownViewer markdown={markdown} />;
}
